import React, { useState, useRef, useEffect, useMemo, useContext, useCallback, forwardRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import { SiteContext } from '@arcteryx/components-contexts';
import Price from '@arcteryx/components-price';
import 'lazysizes/plugins/attrchange/ls.attrchange';
import { CaptionStyles, Body1, Breakpoint, Body6, ScreenSize, Subtitle3, Body3, Subtitle3Styles, Body6Styles, H6 } from '@arcteryx/components-typography';
import Imgix$1 from 'react-imgix';
import { useTranslation } from 'react-i18next';
import Flickity from 'react-flickity-component';
import { RadioVariant } from '@arcteryx/components-form';
import debounce from 'debounce';
import DataLayer from '@arcteryx/data-layer';

const exclusionList = {
  compare: ["newcolour"],
  crossSell: ["newcolour"],
  pdp: [],
  plp: []
};
const priorityLevels = {
  unisex: 1,
  newcolour: 2,
  exclusive: 3,
  bestseller: 4,
  limitedstock: 5,
  fairtradecertified: 6,
  sustainable: 7
};
const applyBadgeRules = function () {
  let badges = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  let {
    page = "",
    badgesToExclude = exclusionList,
    badgePriority = priorityLevels
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const filteredBadges = badges.filter(badge => typeof badge !== 'string');
  const uniqueBadges = filteredBadges.filter((badge, index, self) => index === self.findIndex(t => t.code === badge.code));
  const processedBadges = uniqueBadges.filter(badge => {
    var _a;
    return !((_a = badgesToExclude[page]) === null || _a === void 0 ? void 0 : _a.includes(badge.code));
  }).sort((a, b) => badgePriority[a.code] - badgePriority[b.code]);
  return processedBadges === null || processedBadges === void 0 ? void 0 : processedBadges.slice(0, 3);
};

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectDestructuringEmpty(obj) {
  if (obj == null) throw new TypeError("Cannot destructure " + obj);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

const getStringWidth = str => {
  const span = document.createElement("span");
  span.innerText = str;
  document.body.appendChild(span);
  const width = span.offsetWidth;
  document.body.removeChild(span);
  return width;
};

const calculateBadgeProperties = _ref => {
  let {
    BORDER,
    GAP,
    PADDING_LEFT_RIGHT,
    containerWidth,
    index,
    label,
    rootFontSize,
    runningWidth
  } = _ref;
  const numOfSides = 2;
  const stringWidth = getStringWidth(label);
  const paddingAndBorder = numOfSides * (rootFontSize * PADDING_LEFT_RIGHT + BORDER);
  const totalGapWidth = GAP * index * rootFontSize;
  const badgeWidth = stringWidth + paddingAndBorder;
  const badgeStripWidth = runningWidth + badgeWidth + totalGapWidth;
  const badgeFits = badgeStripWidth <= containerWidth;
  return {
    badgeFits,
    badgeWidth
  };
};

const GAP = 0.5;
const PADDING_LEFT_RIGHT = 0.5;
const BORDER = 1;
const BadgeContainer = styled.div`
  align-items: center;
  align-self: flex-start;
  display: flex;
  flex-flow: row nowrap;
  gap: ${GAP}rem;
  justify-content: center;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 4px;
`;
const BadgeItem = styled.div`
  background-color: var(--white);
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  gap: 12px;
  border: 1px solid var(--inactive-state-grey);
`;
const Label = styled.span`
  ${CaptionStyles}
`;
const BadgeList = _ref => {
  let {
    badges,
    width
  } = _ref;
  const containerWidth = Math.floor(width);
  const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
  let runningWidth = 0;
  return /*#__PURE__*/React.createElement(BadgeContainer, {
    "data-testid": "product-tile__badges",
    className: "product-tile__badges"
  }, badges === null || badges === void 0 ? void 0 : badges.map((_ref2, index) => {
    let {
      label
    } = _ref2;
    const {
      badgeFits,
      badgeWidth
    } = calculateBadgeProperties({
      BORDER,
      GAP,
      PADDING_LEFT_RIGHT,
      containerWidth,
      index,
      rootFontSize,
      runningWidth,
      label
    });
    if (!badgeFits) {
      return null;
    }
    runningWidth += badgeWidth;
    return (
      /*#__PURE__*/
      // TODO: use new core-ui Label component <Label className="product-tile__badge-item" key={index} label={label} variant="badge" />
      React.createElement(BadgeItem, {
        className: "product-tile__badge-item",
        key: index
      }, /*#__PURE__*/React.createElement(Label, null, label))
    );
  }));
};

const _excluded$1 = ["src"];
const withArcteryxImgixSrcUrl = Component => {
  const HOC = _ref => {
    let {
        src
      } = _ref,
      props = _objectWithoutProperties(_ref, _excluded$1);
    const url = (src === null || src === void 0 ? void 0 : src.replace("https://images.arcteryx.com", "https://images-dynamic-arcteryx.imgix.net")) || src;
    return /*#__PURE__*/React.createElement(Component, _extends({
      src: url
    }, props));
  };
  HOC.displayName = "ComponentWithArcteryxImgixSrcUrl";
  return HOC;
};
var Imgix = withArcteryxImgixSrcUrl(Imgix$1);

const MainImageContainer = styled.div`
  @media (hover: hover) and (pointer: fine) {
    &.has-alt-image:hover {
      .alt-image {
        display: ${props => props.market === "leaf" ? "none" : "block"};
      }
      .product-badge {
        display: none;
      }
    }
  }
`;
const Inner$1 = styled.span.attrs(props => ({
  ref: props.innerRef
}))`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  max-width: ${props => props.campaignImage || props.market === "outdoor" ? "none" : "232px"};
  min-height: ${props => props.market === "leaf" ? "345px" : "415px"};
  margin: 0 auto;
  overflow: hidden;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-clip: content-box;
  position: relative;

  img {
    width: 100%;
    @media screen and (max-width: 480px) {
      width: 90%;
    }
  }

  .alt-image {
    display: none;
    position: absolute;
    width: 100%;

    img {
      object-fit: cover;
      width: 100%;
      height: 100% !important; // need important so it doesn't get overwritten by global styles
    }
  }

  @media screen and (max-width: 768px) {
    padding-bottom: 0;
    min-height: 260px;
  }
  @media screen and (min-width: 414px) and (max-width: 768px - 1) {
    padding: 0 0 15px;
  }
`;
const MainImage = _ref => {
  let {
    colour,
    altText = "",
    altImage = null,
    imageLoadDebounceTime,
    lazyload = true,
    market,
    campaignImage,
    page = "" // used to limit badges on certain areas of the site, e.g. compare, crossSell, pdp, plp
  } = _ref;
  const {
    badges,
    image
  } = colour || {};
  const [showImage, setShowImage] = useState(!imageLoadDebounceTime);
  const [innerWidth, setInnerWidth] = useState(0);
  const innerRef = useRef(null);
  useEffect(() => {
    const updateWidth = () => {
      if (innerRef.current) {
        const width = innerRef.current.offsetWidth;
        setInnerWidth(width);
      }
    };
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowImage(true);
    }, imageLoadDebounceTime);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [imageLoadDebounceTime]);
  const attributeConfig = lazyload ? {
    src: "data-src",
    srcSet: "data-srcset",
    sizes: "data-sizes"
  } : null;
  const isValidImage = imageUrl => {
    return typeof imageUrl !== "undefined";
  };
  const selectedBadges = applyBadgeRules(badges, {
    page
  });
  const showBadges = (selectedBadges === null || selectedBadges === void 0 ? void 0 : selectedBadges.length) > 0;
  return /*#__PURE__*/React.createElement(MainImageContainer, {
    className: `qa--product-tile__main-image-container ${altImage !== null && altImage !== void 0 && altImage.url ? "has-alt-image" : ""}`,
    market: market
  }, /*#__PURE__*/React.createElement(Inner$1, {
    ref: innerRef,
    className: "product-tile__main-image",
    market: market,
    campaignImage: campaignImage
  }, altImage !== null && altImage !== void 0 && altImage.url ? /*#__PURE__*/React.createElement("span", {
    className: "alt-image"
  }, /*#__PURE__*/React.createElement(Imgix, {
    src: altImage.url,
    width: 450,
    className: "lazyload",
    imgixParams: {
      auto: "format,compress",
      fit: "fill",
      q: 75
    },
    htmlAttributes: {
      alt: altText
    },
    attributeConfig: attributeConfig
  })) : "", showImage && (isValidImage(campaignImage) || isValidImage(image === null || image === void 0 ? void 0 : image.url)) && /*#__PURE__*/React.createElement(Imgix, {
    src: campaignImage || image.url,
    className: "primary lazyload",
    width: 450,
    imgixParams: {
      auto: "format,compress",
      fit: "fill",
      q: 75
    },
    htmlAttributes: {
      alt: altText
    },
    attributeConfig: attributeConfig
  }), showBadges && /*#__PURE__*/React.createElement(BadgeList, {
    badges: selectedBadges,
    width: innerWidth
  })));
};

const RatingsWrap = styled.div`
  display: flex;
  justify-content: ${props => props.alignLeft ? "flex-start" : "center"};
  align-items: center;
  gap: 6px;
`;
const starSVG = props => {
  const fill = props.filled ? "#990000" : "#e9e9e9";
  return encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 25 25" focusable="false" style="width: 15px; height: 15px;">
    <polygon style="fill: ${fill};" points="25 9.12 15.5669599 9.12 12.512219 0 9.40860215 9.12 0 9.12 7.55131965 14.856 4.47214076 24 12.512219 18.216 20.5522972 24 17.4731183 14.856" />
  </svg>`);
};
const Stars = styled.span`
  display: block;
  background-image: url('data:image/svg+xml;utf8,${starSVG}');
  background-repeat: repeat;
  width: 75px;
  height: 15px;
`;
const Ratings = _ref => {
  let {
    rating,
    reviewCount,
    className,
    alignLeft
  } = _ref;
  const width = `${100 * rating / 5}%`;
  return /*#__PURE__*/React.createElement(RatingsWrap, {
    className: className,
    alignLeft: alignLeft
  }, /*#__PURE__*/React.createElement(Stars, null, /*#__PURE__*/React.createElement(Stars, {
    filled: true,
    style: {
      width
    }
  })), /*#__PURE__*/React.createElement(Body1, {
    tag: "span"
  }, "(", reviewCount, ")"));
};

const Flag = styled.div`
  min-height: 24px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: 8px;
  padding-bottom: 10px;

  ${Breakpoint("md")`
    gap: 12px;
  `}
`;
const TextFlag = styled(Body6).attrs({
  tag: "span"
})`
  color: ${props => {
  var _props$textColor;
  return (_props$textColor = props.textColor) !== null && _props$textColor !== void 0 ? _props$textColor : "var(--colour-black)";
}};
  line-height: 14px;
  text-align: inherit;
  text-transform: uppercase;
  display: block;
`;
const ProductFlag = props => {
  const {
    product,
    hideProductFlag
  } = props;
  const {
    t
  } = useTranslation("components-product-tile");
  const collection = useMemo(() => {
    var _product$collection;
    return product === null || product === void 0 ? void 0 : (_product$collection = product.collection) === null || _product$collection === void 0 ? void 0 : _product$collection.toLowerCase();
  }, [product === null || product === void 0 ? void 0 : product.collection]);
  const renderCollection = useMemo(() => collection !== null && collection !== void 0 && collection.includes("pro") ? null : collection, [collection]);
  return /*#__PURE__*/React.createElement(Flag, {
    "data-testid": "product-tile__flag",
    className: `qa--product-tile__flag`,
    market: props.market
  }, !hideProductFlag && /*#__PURE__*/React.createElement(React.Fragment, null, product.isNew ? /*#__PURE__*/React.createElement(TextFlag, null, t("New"), " ") : null, product.isRevised ? /*#__PURE__*/React.createElement(TextFlag, null, t("Revised"), " ") : null, product.isPro ? /*#__PURE__*/React.createElement(TextFlag, null, t("Pro Exclusive")) : null), /*#__PURE__*/React.createElement(TextFlag, {
    textColor: "var(--medium-grey)"
  }, renderCollection));
};

const ThumbnailWrap = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: transparent;
  box-sizing: border-box;
  width: 3.466rem;
  height: auto;
  padding: 0 0.466rem;
  margin: 0;
  img {
    width: ${props => props.alignLeft ? "85%" : "100%"};
    height: auto;
    min-width: 2.533rem;
    @media screen and (max-width: 580px) {
      .product-tile__colors--flickity & {
        width: 90%;
        height: auto;
      }
    }
    @media screen and (max-width: 412px) {
      min-width: 1.933rem !important;
    }
  }
  @media screen and (max-width: 480px) {
    width: 2.866rem;
    padding: 0 0.466rem;
  }

  .product-tile__colors--flickity & {
    width: 25%;
    height: auto;
    min-height: 4rem;
    @media screen and (max-width: 768px) {
      width: ${props => props.alignLeft ? "3.566rem" : "3.466rem"};
      min-height: 4rem;
    }
    @media screen and (max-width: 412px) {
      width: 2.866rem !important;
      min-height: 3.466rem !important;
    }
  }
`;
const Thumbnail = _ref => {
  var _colour$thumbnail, _colour$image;
  let {
    colour,
    onClick,
    alignLeft,
    lazyload
  } = _ref;
  const attributeConfig = lazyload ? {
    src: "data-src",
    srcSet: "data-srcset",
    sizes: "data-sizes"
  } : null;
  const thumbnailUrl = ((_colour$thumbnail = colour.thumbnail) === null || _colour$thumbnail === void 0 ? void 0 : _colour$thumbnail.url) || ((_colour$image = colour.image) === null || _colour$image === void 0 ? void 0 : _colour$image.url);
  return /*#__PURE__*/React.createElement(ThumbnailWrap, {
    onClick: onClick,
    className: `qa--product-tile__thumbnail ${colour.primaryColour}`,
    alignLeft: alignLeft
  }, /*#__PURE__*/React.createElement(Imgix, {
    src: thumbnailUrl,
    htmlAttributes: {
      alt: colour.label
    },
    width: 50,
    className: "lazyload",
    attributeConfig: attributeConfig
  }));
};

// Not full refactored yet.
const ThumnailSliderWrap = styled.div`
  ${Breakpoint("xs")`
    margin-top:  ${props => !props.hasMarginTop ? "0" : "1px"};
    `}
  ${Breakpoint("sm")`
    margin-top:  1px;
    `}
  .product-tile__colors {
    display: flex;
    justify-content: ${props => props.alignLeft ? "flex-start" : "center"};
    margin: ${props => props.alignLeft ? "0" : "0 auto"};
    max-width: ${props => props.alignLeft ? "19.666rem" : "18.666rem"};
    min-height: 4.733rem;
    width: ${props => props.alignLeft ? "93%" : "auto"};
    transition: 400ms ease;
    opacity: 1;
    ${Breakpoint("md")`
      margin-top: 0;
      width: 100%;
    `}
    &.flickity-enabled {
      overflow: hidden;
      transition: 300ms ease;
      position: relative;
      &:focus {
        outline: none;
      }
      .flickity-viewport {
        overflow: hidden;
        position: relative;
        height: 100% !important; // need to overwrite flickity adding height
        min-height: 4.733rem;
        .flickity-slider {
          display: flex;
          align-items: center;
          height: 100%;
          left: 0 !important;
        }
      }
      .flickity-button {
        position: absolute;
        background: transparent;
        border: none;
        color: var(--colour-grey-dark);
        &:hover {
          cursor: pointer;
        }
        &:focus {
          outline: none;
          box-shadow: none;
        }
        &:active {
          opacity: 0.6;
        }
        &:disabled {
          opacity: 0.3;
          cursor: auto;
          pointer-events: none;
        }
      }
      .flickity-button-icon {
        fill: currentColor;
      }
      .flickity-prev-next-button {
        top: 50%;
        width: 2.933rem;
        height: 2.933rem;
        border-radius: 0%;
        transform: translateY(-50%);
        &.previous {
          ${props => props.alignLeft ? "right: 0.666rem;" : "left: 0.666rem;"};
        }
        &.next {
          ${props => props.alignLeft ? "right: -0.5rem;" : "right: 0.666rem;"};
        }
        .flickity-button-icon {
          position: absolute;
          left: 20%;
          top: 20%;
          width: 60%;
          height: 60%;
        }
      }
    }
  }
  .product-tile__colors.flickity-enabled.is-draggable .flickity-viewport {
    cursor: pointer;
  }

  .product-tile__colors.flickity-enabled {
    display: block;
    padding: ${props => props.alignLeft ? "0 1.33rem 0 0;" : "0 1.333rem"};
    height: 4.733rem;
    max-height: initial;
    margin: ${props => props.alignLeft ? "0" : "auto"};
    ${Breakpoint("md")`
      ${props => props.alignLeft ? "padding: 0 2.2rem 0 0;" : "padding: 0 2.2rem;"};
    `}
    ${Breakpoint("lg")`
      ${props => props.alignLeft ? "padding: 0 2.666rem 0 0;" : "padding: 0 2.666rem;"};
    `}
    .flickity-prev-next-button {
      width: 2.333rem;
      @media screen and (max-width: ${ScreenSize.md}px) {
        width: 1.733rem;
        padding: 0;

        &.previous {
          ${props => props.alignLeft ? "" : "left: 0;"};
        }
        &.next {
          ${props => props.alignLeft ? "" : "right: 0;"};
        }
      }
      @media screen and (max-width: ${ScreenSize.sm}px) {
        &.previous {
          ${props => props.alignLeft ? "right: 0.125rem" : "left: 0;"};
        }
      }
    }
  }
`;
const ThumbnailSlider = _ref => {
  let {
    colours,
    doThumbnailClick,
    alignLeft,
    lazyload,
    hasMarginTop
  } = _ref;
  const minImagesForFlickity = 5;
  const flickityOptions = {
    pageDots: false,
    draggable: true,
    cellAlign: "left",
    accessibility: false,
    contain: true,
    groupCells: true
  };
  const renderColours = key => colours === null || colours === void 0 ? void 0 : colours.map(colour => /*#__PURE__*/React.createElement(Thumbnail, {
    key: `${key}-${colour.label}`,
    colour: colour,
    onClick: () => doThumbnailClick(colour),
    alignLeft: alignLeft,
    lazyload: lazyload
  }));
  return /*#__PURE__*/React.createElement(ThumnailSliderWrap, {
    className: "qa--product-tile__colour-thumbnails",
    alignLeft: alignLeft,
    hasMarginTop: hasMarginTop
  }, (colours === null || colours === void 0 ? void 0 : colours.length) >= minImagesForFlickity ? /*#__PURE__*/React.createElement(Flickity, {
    className: "product-tile__colors product-tile__colors--flickity",
    elementType: "span",
    options: flickityOptions,
    disableImagesLoaded: false,
    reloadOnUpdate: true
  }, renderColours("flickity")) : /*#__PURE__*/React.createElement("span", {
    className: "product-tile__colors"
  }, renderColours("basic")));
};
var ThumbnailSlider$1 = /*#__PURE__*/React.memo(ThumbnailSlider);

const StyledCompare = styled(RadioVariant)`
  margin: 1rem 0 0;
  .qa-control-label {
    margin: ${props => props.alignLeft ? "0 0.75rem" : "0 auto"};
    text-transform: uppercase;
  }
`;
const Compare = _ref => {
  let {
    productId,
    compareChecked,
    shouldDisableCompare,
    alignLeft,
    onCompareToggle = () => {
      // default function
    }
  } = _ref;
  const {
    t
  } = useTranslation("components-product-tile");
  const handleClick = checked => {
    onCompareToggle(productId, checked);
  };
  return /*#__PURE__*/React.createElement(StyledCompare, {
    labelText: t("Compare"),
    handleClick: handleClick,
    checked: compareChecked,
    name: `compare-${productId}`,
    value: `compare-${productId}`,
    disabled: compareChecked ? false : shouldDisableCompare,
    alignLeft: alignLeft
  });
};

const leafProductSkus = ["X000005038", "X000005093", "X000005053", "X000005108", "X000005024", "X000004647", "X000005259", "X000004676"];

const Prices$1 = styled.div`
  display: flex;
  justify-content: ${props => props.alignLeft ? "flex-start" : "center"};
  align-items: center;
`;
const DiscountPrice$1 = styled(Price)`
  color: var(--warning-red);
`;
const RangeSpan = styled(Subtitle3).attrs({
  tag: "span"
})`
  color: var(--warning-red);
    line-height: 24px;
`;
const PriceRange = _ref => {
  let props = _extends({}, (_objectDestructuringEmpty(_ref), _ref));
  const {
    discountPrice,
    price,
    minDiscountPrice,
    currencyCode,
    minPrice
  } = useMemo(() => props.product, [props.product]);
  const maxDiscountPrice = discountPrice && discountPrice < price && minDiscountPrice < discountPrice ? discountPrice : price;
  return /*#__PURE__*/React.createElement(Prices$1, null, /*#__PURE__*/React.createElement(RangeSpan, null, /*#__PURE__*/React.createElement(DiscountPrice$1, {
    price: minDiscountPrice,
    currency: currencyCode,
    className: "qa--product-tile__minRange-price"
  }), minDiscountPrice !== minPrice && minDiscountPrice !== discountPrice && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", null, " - "), /*#__PURE__*/React.createElement(DiscountPrice$1, {
    price: maxDiscountPrice,
    currency: currencyCode,
    className: "qa--product-tile__discount-price"
  }))));
};

const FlickityStyle = createGlobalStyle`
  /*! Flickity v3.0.0
  https://flickity.metafizzy.co
  ---------------------------------------------- */

  .flickity-enabled {
    position: relative;
  }

  .flickity-enabled:focus { outline: none; }

  .flickity-viewport {
    overflow: hidden;
    position: relative;
    height: 100%;
    touch-action: pan-y;
  }

  .flickity-slider {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 0;
  }

  .flickity-rtl .flickity-slider {
    left: unset;
    right: 0;
  }

  /* draggable */

  .flickity-enabled.is-draggable {
    -webkit-tap-highlight-color: transparent;
    user-select: none;
  }

  .flickity-enabled.is-draggable .flickity-viewport {
    cursor: move;
    cursor: grab;
  }

  .flickity-enabled.is-draggable .flickity-viewport.is-pointer-down {
    cursor: grabbing;
  }

  /* ---- flickity-cell ---- */

  .flickity-cell {
    position: absolute;
    left: 0;
  }

  .flickity-rtl .flickity-cell {
    left: unset;
    right: 0;
  }

  /* ---- flickity-button ---- */

  .flickity-button {
    position: absolute;
    background: hsl(0 0% 100% / 75%);
    border: none;
    color: #333;
  }

  .flickity-button:hover {
    background: white;
    cursor: pointer;
  }

  .flickity-button:focus {
    outline: none;
    box-shadow: 0 0 0 5px #19F;
  }

  .flickity-button:active {
    opacity: 0.6;
  }

  .flickity-button:disabled {
    opacity: 0.3;
    cursor: auto;
    /* prevent disabled button from capturing pointer up event. #716 */
    pointer-events: none;
  }

  .flickity-button-icon {
    fill: currentColor;
  }

  /* ---- previous/next buttons ---- */

  .flickity-prev-next-button {
    top: 50%;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    /* vertically center */
    transform: translateY(-50%);
  }

  .flickity-prev-next-button.previous { left: 10px; }
  .flickity-prev-next-button.next { right: 10px; }
  /* right to left */
  .flickity-rtl .flickity-prev-next-button.previous {
    left: auto;
    right: 10px;
  }

  .flickity-rtl .flickity-prev-next-button.next {
    right: auto;
    left: 10px;
  }

  .flickity-prev-next-button .flickity-button-icon {
    position: absolute;
    left: 20%;
    top: 20%;
    width: 60%;
    height: 60%;
  }

  /* ---- page dots ---- */

  .flickity-page-dots {
    position: absolute;
    width: 100%;
    bottom: -25px;
    text-align: center;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
  }

  .flickity-rtl .flickity-page-dots { direction: rtl; }

  .flickity-page-dot {
    display: block;
    width: 10px;
    height: 10px;
    padding: 0;
    margin: 0 8px;
    background: hsl(0 0% 20% / 25%);
    border-radius: 50%;
    cursor: pointer;
    appearance: none;
    border: none;
    text-indent: -9999px;
    overflow: hidden;
  }

  .flickity-rtl .flickity-page-dot {
    text-indent: 9999px;
  }

  .flickity-page-dot:focus {
    outline: none;
    box-shadow: 0 0 0 5px #19F;
  }

  .flickity-page-dot.is-selected {
    background: hsl(0 0% 20% / 100%);
  }
`;

const _excluded = ["onClick", "onMouseDown", "onContextMenu", "imageLoadDebounceTime", "hideThumbnails", "hideProductFlag", "hoverBorder", "lazyload", "alignLeft", "campaignImage", "hasMarginTop", "discountPricesColumn"];
const ProductTileWrap = styled.div`
  min-height: 29.333rem;
  margin: 0 auto;
  width: 100%;
  color: var(--black);
  border: 1px solid transparent;
  padding: ${props => props.hoverBorder ? "1rem 0" : 0};
  ${props => props.hoverBorder ? Breakpoint("sm")`
      &:hover {
        border-color: var(--colour-grey-pinline);
      }
    }
  ` : ""}
`;
const Inner = styled.div`
  background: var(--white);
  width: 90%;
  margin: 0 auto;
  position: relative;
  text-align: ${props => props.alignLeft ? "left" : "center"};
  user-select: none;

  ${props => props.market === "leaf" ? `@media screen and (max-width: 768px) {
          padding-top: 20px;
        }` : ""}
`;
const Name = styled(Body3).attrs({
  tag: "div"
})`
  text-align: inherit;
  margin-bottom: 16px;
`;
const Description = styled(Body1).attrs({
  tag: "div"
})`
  text-align: inherit;
  max-width: 16.666rem;
  min-height: 3rem;
  margin: ${props => props.alignLeft ? "0 0 16px" : "0 auto 16px"};
  ::first-letter {
    text-transform: capitalize;
  }
`;
const Anchor = styled.a`
  display: block;
  color: var(--colour-black);
  text-decoration: none !important;
`;
const DetailsAnchor = styled(Anchor)`
  ${Breakpoint("xs")`
  margin-top:  ${props => !props.hasMarginTop ? "8px" : "var(--space-blue)"};
  `}
  ${Breakpoint("sm")`
  margin-top:  var(--space-blue);
  `}
`;
const PricesAndRatings = styled.div`
  margin-top: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  align-items: center;
`;
const Prices = styled.div`
  display: flex;
  justify-content: ${props => props.alignLeft ? "flex-start" : "center"};
  align-items: center;
  flex-direction: column;
  gap: 8px;
    
  ${Breakpoint("xl")`
    gap: 10px;
    flex-direction: ${props => props.discountPricesColumn ? "column" : " row"};
  `};
`;
const OriginalPrice = styled(Price)`
  ${Subtitle3Styles};
`;
const MarkdownPrice = styled(Price)`
  ${Body6Styles};
  text-decoration: line-through;
  display: inline-block;
  color: var(--medium-grey)
`;
const DiscountPrice = styled(Price)`
  ${Subtitle3Styles};
  color: var(--warning-red);
`;

// ProductTile is a React component that represents a single product in a list or grid of products.
// It is responsible for displaying the product's image, name, price, and other relevant information.
const ProductTile = _ref => {
  var _product$slug, _product$slug$replace, _product$colourOption;
  let {
      onClick,
      onMouseDown,
      onContextMenu,
      imageLoadDebounceTime,
      hideThumbnails,
      hideProductFlag = false,
      hoverBorder = true,
      lazyload = true,
      alignLeft,
      campaignImage,
      hasMarginTop,
      discountPricesColumn = false
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const {
    market
  } = useContext(SiteContext);
  const product = useMemo(() => props.product, [props.product]);
  const nameSlug = product === null || product === void 0 ? void 0 : (_product$slug = product.slug) === null || _product$slug === void 0 ? void 0 : (_product$slug$replace = _product$slug.replace("/", "-")) === null || _product$slug$replace === void 0 ? void 0 : _product$slug$replace.toLowerCase();
  const [selectedColour, setSelectedColour] = useState(product.selectedColour);
  const {
    discountPrice,
    price,
    minPrice,
    minDiscountPrice = 0,
    id
  } = product;

  // Handle base price
  const isMarkdownPrice = minDiscountPrice && minDiscountPrice !== discountPrice && minPrice <= price && minDiscountPrice !== minPrice;
  const BasePrice = discountPrice && discountPrice < price && minDiscountPrice <= discountPrice || isMarkdownPrice ? MarkdownPrice : OriginalPrice;
  // Conditionals for showing certain prices
  const showPrice = !leafProductSkus.includes(id);
  const isDiscountPrice = !minDiscountPrice && discountPrice && discountPrice < price;
  const isPriceRange = Boolean(minDiscountPrice && minDiscountPrice < minPrice);
  const doThumbnailClick = useCallback(colour => {
    product.colourId = colour.value;
    setSelectedColour(colour);
  }, [setSelectedColour]);
  const onProductTileClick = e => {
    if (onClick) {
      onClick(e, product);
    }
  };
  const onProductTileMouseDown = e => {
    if (onMouseDown) {
      // Fixes SWAT-7488
      e.preventDefault();
      onMouseDown(e, product);
    }
  };
  const onProductContextMenu = e => {
    if (onContextMenu) {
      onContextMenu(e, product);
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ProductTileWrap, {
    id: nameSlug ? nameSlug : product.id,
    hoverBorder: hoverBorder
  }, /*#__PURE__*/React.createElement(FlickityStyle, null), /*#__PURE__*/React.createElement(Inner, {
    market: market,
    alignLeft: alignLeft
  }, /*#__PURE__*/React.createElement(Anchor, {
    className: "qa--product-tile__link",
    href: product.url,
    onClick: onProductTileClick,
    onContextMenu: onProductContextMenu,
    onMouseDown: onProductTileMouseDown
  }, /*#__PURE__*/React.createElement(MainImage, _extends({
    productCode: product.id,
    productUrl: product.url,
    colour: selectedColour,
    altText: product.name,
    altImage: product.mainImage,
    imageLoadDebounceTime: imageLoadDebounceTime,
    lazyload: lazyload,
    market: market,
    campaignImage: campaignImage
  }, props))), !hideThumbnails && /*#__PURE__*/React.createElement(ThumbnailSlider$1, {
    colours: (_product$colourOption = product.colourOptions) === null || _product$colourOption === void 0 ? void 0 : _product$colourOption.options,
    doThumbnailClick: doThumbnailClick,
    alignLeft: alignLeft,
    lazyload: lazyload,
    hasMarginTop: hasMarginTop
  }), /*#__PURE__*/React.createElement(DetailsAnchor, {
    className: "product-tile-details qa--product-tile__link",
    href: product.url,
    onClick: onProductTileClick,
    onMouseDown: onProductTileMouseDown,
    market: market,
    hasMarginTop: hasMarginTop
  }, /*#__PURE__*/React.createElement(ProductFlag, {
    product: product,
    market: market,
    hideProductFlag: hideProductFlag
  }), /*#__PURE__*/React.createElement(Name, {
    className: "product-tile-name",
    market: market
  }, product.name), product.shortDescription && /*#__PURE__*/React.createElement(Description, {
    alignLeft: alignLeft
  }, product.shortDescription), /*#__PURE__*/React.createElement(PricesAndRatings, null, showPrice && /*#__PURE__*/React.createElement(Prices, {
    className: "qa--product-tile__prices",
    market: market,
    alignLeft: alignLeft,
    discountPricesColumn: discountPricesColumn
  }, /*#__PURE__*/React.createElement(BasePrice, {
    price: product.price,
    currency: product.currencyCode,
    className: "qa--product-tile__original-price"
  }), isDiscountPrice && /*#__PURE__*/React.createElement(DiscountPrice, {
    price: product.discountPrice,
    currency: product.currencyCode,
    className: "qa--product-tile__discount-price"
  }), isPriceRange && /*#__PURE__*/React.createElement(PriceRange, {
    product: product
  })), market === "outdoor" && /*#__PURE__*/React.createElement(Ratings, {
    rating: product.rating,
    reviewCount: product.reviewCount,
    className: "qa--product-tile__rating",
    alignLeft: alignLeft
  })))), props.onCompareToggle && /*#__PURE__*/React.createElement(Compare, {
    productId: product.id,
    onCompareToggle: props.onCompareToggle,
    compareChecked: props.compareChecked,
    shouldDisableCompare: props.shouldDisableCompare,
    alignLeft: alignLeft
  })));
};

const MatchListItem = styled.span`
  background-color: var(--white);
  display: inline-flex;
  align-items: center;
  padding: 0px 8px 2px;
  gap: 12px;
  border: 1px solid var(--inactive-state-grey);
  cursor: pointer;

  margin: 0.2rem;
  justify-content: center;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  &.not-matched {
    background-color: var(--background-grey);
    cursor: default;

    span {
      color: var(--inactive-state-grey);
    }
  }
`;
const MatchListWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
`;
const MatchWrapper = styled.div`
  ${Breakpoint("xs")`
  margin: var(--space-green) var(--space-blue) 0px;
`}
  ${Breakpoint("md")`
  margin: var(--space-blue);
`}
`;
const MatchText = styled(H6)`
  text-align: center;
  display: block;
  margin-bottom: 16px;
`;
const ListItem = _ref => {
  let {
    name,
    match,
    highlight
  } = _ref;
  let listItemClassName = "not-matched";
  if (match) {
    listItemClassName = highlight ? "highlighted" : "matched";
  }
  return (
    /*#__PURE__*/
    // TODO: use core-ui Label (finder) component
    React.createElement(MatchListItem, {
      "data-testid": "match-list-item",
      className: listItemClassName
    }, /*#__PURE__*/React.createElement(Body1, {
      tag: "span"
    }, name))
  );
};
const MatchList = _ref2 => {
  let {
    score,
    matchText,
    matchPreferences
  } = _ref2;
  return /*#__PURE__*/React.createElement(MatchWrapper, null, /*#__PURE__*/React.createElement(MatchText, {
    tag: "span",
    "aria-label": `${score}% ${matchText}`
  }, score, "% ", matchText), /*#__PURE__*/React.createElement(MatchListWrapper, null, matchPreferences.map((matchPreference, index) => /*#__PURE__*/React.createElement(ListItem, _extends({
    key: index
  }, matchPreference)))));
};

const MatchedProductTile = _ref => {
  let {
    product,
    hasMarginTop
  } = _ref;
  const {
    matchPreferences,
    matchText,
    score
  } = product;
  return /*#__PURE__*/React.createElement(React.Fragment, null, (matchPreferences === null || matchPreferences === void 0 ? void 0 : matchPreferences.length) > 0 && /*#__PURE__*/React.createElement(MatchList, {
    matchPreferences: matchPreferences,
    matchText: matchText,
    score: score
  }), /*#__PURE__*/React.createElement(ProductTile, {
    product: product,
    hasMarginTop: hasMarginTop
  }));
};

const MOBILE_BREAKPOINT = 480;
const StyledFlickity = styled(Flickity)`
  .flickity-viewport {
    min-height: 425px;

    ${Breakpoint("md")`
      min-height: 560px;
    `}
  }
`;
const flickityOptions = {
  wrapAround: true,
  pageDots: false,
  groupCells: false,
  initialIndex: 1
};
const TileContainer = styled.div`
  display: block;
  width: 60%;

  display: flex;
  align-items: stretch;
  min-height: 100%;

  > div {
    display: flex;
    align-items: stretch;
    min-height: 100%;

    > div {
      display: flex;
      flex-direction: column;
    }
  }

  @media screen and (min-width: ${MOBILE_BREAKPOINT}px) {
    width: 40%;
  }

  ${Breakpoint("lg")`
    width: ${_ref => {
  let {
    thresholdForFlickity
  } = _ref;
  return thresholdForFlickity === 4 ? "25%" : "33.33%";
}};
  `}

  .product-tile-slider:not(.product-tile-slider-count-1) & {
    @media screen and (max-width: ${MOBILE_BREAKPOINT - 1}px) {
      .product-tile-details {
        opacity: 0;
        transition: opacity 0.3s;
      }
      &.is-selected {
        .product-tile-details {
          opacity: 1;
        }
      }
    }
  }
`;
const addToViewedArray = (viewedArr, crossDataAnalyticsArr, idx, setViewedProducts, increment) => {
  let currentIdx = idx;
  if (currentIdx === 1 && !increment) {
    currentIdx = idx - 1;
  }
  if (currentIdx === 1 && increment) {
    currentIdx = idx + 1;
  }
  let prevIdx = currentIdx - 1 < 0 ? crossDataAnalyticsArr.length - 1 : currentIdx - 1;
  let nextIdx = currentIdx + 1 > crossDataAnalyticsArr.length - 1 ? 0 : currentIdx + 1;
  let itemsToAdd = [];
  [prevIdx, currentIdx, nextIdx].forEach(index => {
    if (!viewedArr.find(p => p._model.productInfo.modelNumber === crossDataAnalyticsArr[index]._model.productInfo.modelNumber)) {
      itemsToAdd.push(crossDataAnalyticsArr[index]);
    }
  });
  setViewedProducts(state => [...state, ...itemsToAdd]);
};
const pushEvent = debounce(viewedProducts => {
  DataLayer.Event.relatedProductsViewed(viewedProducts);
}, 2000); //two second delay

const ProductTileList = /*#__PURE__*/forwardRef((_ref2, externalRef) => {
  let {
    products,
    documentWidth,
    forSlider = false,
    lazyloadSliderImages = true,
    productTileProps = {},
    thresholdForFlickity,
    groupCells = false,
    hasMarginTop
  } = _ref2;
  // We probably don't want to lazy load the ProductTile image if it's in a slider. This is because Flickity
  // erroneously calculates the height it needs if the images load later. However, let the consumer decide
  // by passing in a prop to explicitly set lazyload false if
  const lazyload = !forSlider || lazyloadSliderImages;
  const {
    crossSellAnalyticsData
  } = productTileProps;
  const [viewedProducts, setViewedProducts] = useState([]);
  const sliderProducts = useMemo(() => {
    let productsForSlider = products && products.length ? products.slice() : [];
    if (forSlider && products.length === 2) {
      // special case to get phantom slides so we can see before and after preview on mobile.
      productsForSlider = [...productsForSlider, ...productsForSlider];
    }
    return productsForSlider;
  }, [forSlider, products]);
  useEffect(() => {
    if (crossSellAnalyticsData) {
      setViewedProducts(crossSellAnalyticsData.slice(0, 3));
    }
  }, [crossSellAnalyticsData]);
  const tiles = sliderProducts.map(product => /*#__PURE__*/React.createElement(TileContainer, {
    className: "product-tile-slide",
    thresholdForFlickity: thresholdForFlickity,
    key: product.id
  }, /*#__PURE__*/React.createElement(ProductTile, _extends({
    product: product
  }, productTileProps, {
    lazyload: lazyload,
    hoverBorder: false,
    hasMarginTop: hasMarginTop
  }))));
  const flickityCallback = carouselRef => {
    if (externalRef) {
      externalRef.current = carouselRef;
    }
  };
  useEffect(() => {
    if (externalRef && externalRef.current && (viewedProducts === null || viewedProducts === void 0 ? void 0 : viewedProducts.length) > 0) {
      const prevNextHandler = e => {
        let increment = e.path[1].getAttribute("aria-label") === "Previous" ? false : true;
        addToViewedArray(viewedProducts, crossSellAnalyticsData, externalRef.current.selectedIndex, setViewedProducts, increment);
      };
      const prevButton = externalRef.current.prevButton.element;
      const nextButton = externalRef.current.nextButton.element;
      prevButton.addEventListener("click", prevNextHandler, false);
      nextButton.addEventListener("click", prevNextHandler, false);
      return () => {
        prevButton.removeEventListener("click", prevNextHandler, false);
        nextButton.removeEventListener("click", prevNextHandler, false);
      };
    }
  }, [externalRef, viewedProducts]);
  useEffect(() => {
    if ((viewedProducts === null || viewedProducts === void 0 ? void 0 : viewedProducts.length) > 3) {
      //initial length
      pushEvent(viewedProducts);
    }
  }, [viewedProducts === null || viewedProducts === void 0 ? void 0 : viewedProducts.length]);
  const cellAlign = useMemo(() => {
    if (documentWidth < MOBILE_BREAKPOINT) {
      return "center";
    }
    return "left";
  }, [documentWidth]);
  return forSlider ?
  /*#__PURE__*/
  /** Force Re-Render of Flickity whenever cellAlign changes */
  React.createElement(React.Fragment, {
    key: cellAlign
  }, /*#__PURE__*/React.createElement(StyledFlickity, {
    flickityRef: flickityCallback,
    options: _objectSpread2(_objectSpread2({}, flickityOptions), {}, {
      cellAlign,
      groupCells,
      resize: true,
      percentPosition: true,
      imagesLoaded: true
    })
  }, tiles)) : tiles;
});
ProductTileList.displayName = "ProductTileList";

const SliderContainer = styled.div`
  width: 100%;
  overflow: hidden;

  &.non-interactive {
    display: flex;
    justify-content: space-evenly;
  }

  > .flickity-enabled {
    position: relative;
    outline: none;

    .flickity-slider {
      height: 100%;
    }

    .flickity-button {
      background: var(--colour-white);
      border: 1px solid var(--colour-grey-inactive);
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      top: 50%;
      left: 10px;
      width: 48px;
      height: 48px;
      box-shadow: none;
      transform: translateY(-50%);

      .flickity-button-icon {
        height: 100%;
        left: auto;
        position: absolute;
        top: 16px;
        width: 33.333333%;
        height: auto;
      }

      &.next {
        left: auto;

        .flickity-button-icon {
          left: auto;
        }
      }
      &.next:hover,
      &.previous:hover {
        cursor: pointer;
      }
      @media screen and (max-width: 599px) {
        display: none;
      }
    }
  }
`;
const defaultProductTileProps = {
  hideThumbnails: true
};
// 1 product:  PDP https://o-qa.arcteryx.com/jp/en/shop/mens/cerium-sl-hoody
// 2 products: PDP https://o-qa.arcteryx.com/jp/en/shop/mens/atom-lt-hoody
// 3 products: PDP https://arcteryx.com/de/en/shop/womens/cerium-lt-jacket

/**
 *  1 product:   NO Slider
 *  2 products:  Slider up to 768.  >768 no slider
 *  3 products:  Slider up to 768.  >768 no slider
 *  4+ products: Slider always
 */
function ProductTileSlider(_ref) {
  let {
    products,
    onProductClick,
    onProductContextMenu,
    onProductMouseDown,
    lazyloadSliderImages = true,
    productTileProps = {},
    crossSellAnalyticsData,
    thresholdForFlickity = 3,
    hideProductFlag = false,
    groupCells = false
  } = _ref;
  const _productTileProps = _objectSpread2(_objectSpread2({}, defaultProductTileProps), {}, {
    onClick: onProductClick,
    onContextMenu: onProductContextMenu,
    onMouseDown: onProductMouseDown,
    crossSellAnalyticsData,
    hideProductFlag
  }, productTileProps);
  const initialWidth = typeof document !== "undefined" ? document.documentElement.clientWidth : 375; // for server side render, assume mobile size.
  const flickityBreakpoint = 768;
  const requiresFlickity = vw => products.length > 1 && products.length <= thresholdForFlickity && vw < flickityBreakpoint || products.length > thresholdForFlickity;
  const [documentWidth, setDocumentWidth] = useState(initialWidth);
  const [flickityEnabled, setFlickityEnabled] = useState(requiresFlickity(initialWidth));
  const carouselRef = useRef(null);

  // Make responsive - useEffect so we only addEventListener once!
  useEffect(() => {
    const onWindowResize = debounce(() => setDocumentWidth(document.documentElement.clientWidth), 100);
    window.addEventListener("resize", onWindowResize);
    return () => window.removeEventListener("resize", onWindowResize);
  }, []);

  // Determine if we need Flickity or not
  useEffect(() => {
    setFlickityEnabled(requiresFlickity(documentWidth));
  }, [documentWidth, products.length]);
  return /*#__PURE__*/React.createElement(SliderContainer, {
    className: `product-tile-slider ${!flickityEnabled ? "non-interactive" : ""} product-tile-slider-count-${products.length}`
  }, /*#__PURE__*/React.createElement(ProductTileList, {
    products: products,
    documentWidth: documentWidth,
    forSlider: flickityEnabled,
    ref: carouselRef,
    thresholdForFlickity: thresholdForFlickity,
    groupCells: groupCells,
    lazyloadSliderImages: lazyloadSliderImages,
    productTileProps: _productTileProps
  }));
}

var resources_DONOTEDIT = {
  "de": {
    "components-product-tile": {
      "Compare": "Vergleichen",
      "New": "Neu",
      "Pro Exclusive": "",
      "Revised": "Überarbeitet"
    }
  },
  "en": {
    "components-product-tile": {
      "Compare": "",
      "New": "",
      "Pro Exclusive": "",
      "Revised": ""
    }
  },
  "es": {
    "components-product-tile": {
      "Compare": "Compare",
      "New": "Nuevo",
      "Pro Exclusive": "",
      "Revised": "Mejorado"
    }
  },
  "fr": {
    "components-product-tile": {
      "Compare": "Comparer",
      "New": "Nouveau",
      "Pro Exclusive": "",
      "Revised": "Revu"
    }
  },
  "jp": {
    "components-product-tile": {
      "Compare": "比較",
      "New": "新商品",
      "Pro Exclusive": "",
      "Revised": "改良版"
    }
  },
  "no": {
    "components-product-tile": {
      "Compare": "Sammenlign",
      "New": "Ny",
      "Pro Exclusive": "",
      "Revised": "Revidert"
    }
  },
  "zh": {
    "components-product-tile": {
      "Compare": "比较",
      "New": "新货",
      "Pro Exclusive": "Pro人员专属",
      "Revised": "修改"
    }
  }
};

export { MatchedProductTile, ProductTile, ProductTileSlider, applyBadgeRules, resources_DONOTEDIT as i18nResources };
//# sourceMappingURL=index.js.map
